import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Facebook, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      return toast.error("Enter a valid email");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/subscribe", { email });
      toast.success(data.already ? "You're already on the list" : "You're in — welcome to the shop.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-black pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-12 mb-20">
          <div>
            <img src="/logo.png" alt="4th Watch Concepts" className="h-20 w-auto object-contain mb-6" />
            <p className="font-num text-primary tracking-[0.3em] text-xs mb-4">PRECISION. PURPOSE. PERSONAL.</p>
            <p className="font-display text-3xl md:text-4xl leading-tight max-w-md">
              Precision laser engraving for the things you refuse to leave ordinary.
            </p>
          </div>
          <div>
            <p className="font-num uppercase tracking-widest text-xs text-muted-foreground mb-5">Explore</p>
            <ul className="space-y-3 font-body text-foreground/80">
              <li><Link to="/shop" className="link-underline">Shop</Link></li>
              <li><Link to="/quote" className="link-underline">Custom Work</Link></li>
              <li><a href="/#process" className="link-underline">Our Process</a></li>
              <li><a href="/#gallery" className="link-underline">Gallery</a></li>
            </ul>
          </div>
          <div>
            <p className="font-num uppercase tracking-widest text-xs text-muted-foreground mb-5">Connect</p>
            <div className="flex gap-4 mb-6">
              <a href="#" aria-label="Instagram" className="p-2 border border-white/10 hover:border-primary hover:text-primary transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href="#" aria-label="Facebook" className="p-2 border border-white/10 hover:border-primary hover:text-primary transition-colors"><Facebook className="w-4 h-4" /></a>
              <a href="mailto:hello@4thwatchconcepts.com" aria-label="Email" className="p-2 border border-white/10 hover:border-primary hover:text-primary transition-colors"><Mail className="w-4 h-4" /></a>
            </div>
            <p className="font-body text-sm text-muted-foreground">hello@4thwatchconcepts.com</p>
          </div>
        </div>

        <div data-testid="newsletter" className="border border-white/10 bg-[#0d0d0d] p-8 md:p-10 mb-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <p className="font-num uppercase tracking-[0.25em] text-primary text-xs mb-2">Join the shop</p>
            <p className="font-display text-2xl md:text-3xl">Get new drops & 10% off your first order.</p>
          </div>
          <form onSubmit={subscribe} noValidate className="flex w-full md:w-auto max-w-md">
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="flex-1 md:w-72 bg-[#111] border border-white/15 px-4 h-12 font-body text-sm outline-none focus:border-primary transition-colors"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={busy}
              className="btn-forge bg-primary text-primary-foreground px-5 h-12 font-num uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-white disabled:opacity-60"
            >
              Join <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="relative">
          <h2 className="font-display text-[16vw] md:text-[13vw] leading-[0.8] tracking-tight text-outline select-none pointer-events-none">
            4TH WATCH
          </h2>
          <p className="font-num uppercase tracking-[0.3em] text-xs text-muted-foreground mt-2">
            Precision isn't just what we do. It's what we stand for.
          </p>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-muted-foreground font-body">
          <span>© {new Date().getFullYear()} 4th Watch Concepts · EST. 2025 · All rights reserved.</span>
          <span>Non-serialized parts & accessories only · Not an FFL</span>
        </div>
      </div>
    </footer>
  );
}
